"use client";

import Image from "next/image";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";

const getRatingText = (score, t) => {
  if (typeof score !== "number" || score <= 0) return null;
  if (score >= 9) return t("Excellent");
  if (score >= 8) return t("Great");
  if (score >= 7) return t("VeryGood");
  if (score >= 6) return t("Good");
  return null;
};

const PLACEHOLDER_IMAGE =
  "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSIxIiBoZWlnaHQ9IjEiPjxyZWN0IHdpZHRoPSIxIiBoZWlnaHQ9IjEiIGZpbGw9IiNmMGYwZjAiLz48L3N2Zz4=";

/** Geçerli resim URL formatı mı kontrol eder (http/https veya / ile başlayan path) */
function isValidImageUrl(url) {
  if (!url || typeof url !== "string") return false;
  const trimmed = url.trim();
  if (!trimmed) return false;
  try {
    if (trimmed.startsWith("/")) return true;
    const parsed = new URL(trimmed);
    return parsed.protocol === "http:" || parsed.protocol === "https:";
  } catch {
    return false;
  }
}

/**
 * Otel last minute kartı - otel listesinin mobil versiyonu gibi.
 * otelLastMinuteSales API verisi:
 * - City, CountryCode, ImageUrl, Name, SurveyScore, SurveyScoreText, Url
 */
export default function OtelLastMinuteCard({ item = {}, onAnalyticsClick }) {
  const t = useTranslations("hotel");
  const name = item.Name || "";
  const city = item.City || "";
  const countryCode = item.CountryCode || "";
  const imageUrl = isValidImageUrl(item.ImageUrl)
    ? item.ImageUrl.trim()
    : PLACEHOLDER_IMAGE;
  const surveyScore = item.SurveyScore;
  const surveyScoreText = getRatingText(surveyScore, t);
  const starRating = item.StarRating ?? (surveyScore != null ? Math.round(surveyScore) : 0);
  const url = item.Url || "/";

  const location = [city, countryCode].filter(Boolean).join(", ");
  const hasRating =
    surveyScore != null &&
    typeof surveyScore === "number" &&
    surveyScore > 0;

  const handleClick = () => {
    onAnalyticsClick?.(item);
  };

  return (
    <Link
      href={url}
      className="hotel-card-item flex h-full flex-col overflow-hidden border border-[#E1E1EF] rounded-lg bg-white p-4"
      onClick={handleClick}
    >
      <div className="flex flex-col gap-4">
        {/* Resim - LandingCardItem mobil gibi, tek resim (Swiper yok) */}
        <div className="relative h-[182px] w-full flex-shrink-0 overflow-hidden rounded-lg bg-gray-200">
          <Image
            src={imageUrl}
            alt={name}
            fill
            className="object-cover"
            sizes="(max-width: 640px) 100vw, 182px"
            loading="lazy"
            unoptimized={imageUrl.startsWith("http")}
          />
        </div>

        {/* İçerik - LandingCardItem mobil blok (flex flex-col gap-[2px] lg:hidden) */}
        <div className="flex flex-col gap-[2px]">
          <h2 className="text-foreground line-clamp-2 flex flex-wrap items-center gap-x-[6px] text-lg font-semibold">
            {name}
            {starRating > 0 && (
              <span className="flex items-center gap-[1px]">
                {[...Array(Math.min(starRating, 5))].map((_, index) => (
                  <FontAwesomeIcon
                    key={index}
                    icon={byPrefixAndName.fas["star"]}
                    className="text-yellow !h-[12px] !w-[12px]"
                  />
                ))}
              </span>
            )}
          </h2>
          {location && (
            <p className="text-foreground text-sm">{location}</p>
          )}
        </div>

        {hasRating && (
          <div className="flex items-center gap-[6px]">
            <div className="bg-foreground w-[26px] flex-shrink-0 rounded-sm px-[5px] py-[2px] text-center text-xs font-medium text-white">
              {surveyScore}
            </div>
            {surveyScoreText && (
              <span className="text-foreground text-sm font-medium">
                {surveyScoreText}
              </span>
            )}
          </div>
        )}
      </div>
    </Link>
  );
}
